import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  List,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  rem,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconAlertCircle,
  IconApi,
  IconBuildingSkyscraper,
  IconCode,
  IconDatabase,
  IconInfoCircle,
  IconKey,
  IconListDetails,
  IconSettings,
  IconShield,
  IconUsers,
  IconUsersGroup,
} from "@tabler/icons-react";
import { PageHeader } from "../../components/PageHeader";

const useStyles = createStyles((theme) => ({
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  card: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  codeBlock: {
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    fontFamily: "monospace",
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.6,
    overflowX: "auto" as const,
    whiteSpace: "pre" as const,
    color: theme.colors.dark[0],
  },
  mutationBadge: {
    fontFamily: "monospace",
    textTransform: "none" as const,
    fontWeight: 600,
  },
  statusTable: {
    "& td, & th": {
      padding: `${rem(8)} ${rem(12)}`,
    },
  },
}));

const GqlBlock = ({ children }: { children: string }) => {
  const { classes } = useStyles();
  return <div className={classes.codeBlock}>{children.trim()}</div>;
};

const SectionHeader = ({
  icon,
  title,
  id,
}: {
  icon: React.ReactNode;
  title: string;
  id: string;
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.sectionTitle} id={id}>
      <ThemeIcon size="md" radius="md" variant="light" color="cyan">
        {icon}
      </ThemeIcon>
      <Title order={2}>{title}</Title>
    </div>
  );
};

export const OrganizationDocs = () => {
  const { classes } = useStyles();

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Organization Documentation"
        subtitle="Complete reference for organization management and configuration"
        showBreadcrumbs={false}
      />

      {/* Table of Contents */}
      <Card className={classes.card} p="md" radius="md" mb="xl">
        <Text fw={600} mb="sm">
          Table of Contents
        </Text>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#overview">Overview</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#data-ownership">Data Ownership & Isolation</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#configuration">Configuration</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#membership">Membership & Groups</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#apps">Apps & OAuth</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#identity-providers">Identity Providers</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#limits">Limits & Subscriptions</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#graphql">GraphQL API Reference</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#data-model">Data Model</Anchor>
            </List.Item>
          </List>
        </SimpleGrid>
      </Card>

      <Stack gap="xl">
        {/* ===================== OVERVIEW ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconBuildingSkyscraper size={18} />}
            title="Overview"
            id="overview"
          />
          <Text mb="md">
            Organizations are the top-level entity in WellPlayed. They represent
            your company, team, or tournament platform and serve as the
            container for all other resources: tournaments, players, events,
            leaderboards, apps, and more.
          </Text>
          <Text mb="md">
            Organizations are composed of the following core entities:
          </Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`Organization
├── Configuration (custom fields for player profiles)
├── Members[] (users with assigned groups and permissions)
├── Groups[] (permission groups: ADMIN, MEMBER, CONNECTED, ANONYMOUS)
├── Apps[] (OAuth2/OIDC client applications)
├── AttachedApps[] (apps linked to groups with specific permissions)
├── IdentityProviders[] (external login providers)
├── Tournaments[] (competitive events)
└── Subscription & Limits (plan-based resource limits)`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            Each organization is fully isolated. Data (including player
            profiles, tournaments, and events) is only accessible within the
            organization that owns it. API requests are scoped to the
            organization via the <Code>organization-id</Code> header.
          </Alert>
        </div>

        {/* ===================== DATA OWNERSHIP ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconShield size={18} />}
            title="Data Ownership & Isolation"
            id="data-ownership"
          />
          <Text mb="md">
            Organizations enforce strict data isolation across the platform.
            Every API request includes an <Code>organization-id</Code> header
            that scopes all operations to a single organization.
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Data Scoping
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  Player profiles belong to a single organization
                </List.Item>
                <List.Item>
                  Tournaments, events, and leaderboards are organization-scoped
                </List.Item>
                <List.Item>
                  API rate limits are tracked per organization independently
                </List.Item>
                <List.Item>
                  Storage and resource limits are per-organization
                </List.Item>
              </List>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Access Control
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  Organization owner has full control over all resources
                </List.Item>
                <List.Item>
                  Members are assigned to groups with granular permissions
                </List.Item>
                <List.Item>
                  Apps authenticate via OAuth2 with scoped access tokens
                </List.Item>
                <List.Item>
                  Cross-organization data access is not possible
                </List.Item>
              </List>
            </Card>
          </SimpleGrid>

          <Alert icon={<IconAlertCircle />} color="yellow" variant="light">
            A user can belong to multiple organizations. Switching organizations
            changes the <Code>organization-id</Code> header in all subsequent
            API requests, effectively switching the entire data context.
          </Alert>
        </div>

        {/* ===================== CONFIGURATION ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconSettings size={18} />}
            title="Configuration"
            id="configuration"
          />
          <Text mb="md">
            Organization configuration is managed through the Settings page. The
            primary configurable entity is the set of custom fields that define
            the player profile schema for the organization.
          </Text>

          <Title order={4} mb="xs">
            Basic Settings
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <strong>Name</strong> - Organization display name (3-50
              characters, letters and spaces only)
            </List.Item>
            <List.Item>
              <strong>Description</strong> - Brief description of the
              organization (max 256 characters)
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Custom Fields
          </Title>
          <Text mb="sm">
            Custom fields define additional properties on player profiles within
            the organization. Each field has:
          </Text>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>
              <strong>Name</strong> - Display name
            </List.Item>
            <List.Item>
              <strong>Property</strong> - Internal key (used in API filters)
            </List.Item>
            <List.Item>
              <strong>Type</strong> - STRING, NUMBER, BOOLEAN, DATE, COUNTRY,
              EMAIL, PHONE, URL
            </List.Item>
            <List.Item>
              <strong>Order</strong> - Display order
            </List.Item>
            <List.Item>
              <strong>Visibility</strong> - PUBLIC, OWNER, OWNER_OR_PERMISSION,
              or WITH_PERMISSION
            </List.Item>
            <List.Item>
              <strong>Editability</strong> - ALWAYS, ONE_TIME, or WITH_PERMISSION
            </List.Item>
            <List.Item>
              <strong>Required / Unique</strong> - Validation constraints
            </List.Item>
          </List>

          <GqlBlock>
            {`mutation ($name: String!, $description: String!,
         $configuration: OrganizationConfigurationInput!) {
  updateOrganization(organization: {
    name: $name
    description: $description
    configuration: $configuration
  }) { id }
}

# OrganizationConfigurationInput:
# { customFields: [PropertyInput!]! }`}
          </GqlBlock>
        </div>

        {/* ===================== MEMBERSHIP ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconUsers size={18} />}
            title="Membership & Groups"
            id="membership"
          />
          <Text mb="md">
            Organizations have an in-depth user and permissions management
            system. Members are assigned to groups, and groups define the
            permissions available to their members.
          </Text>

          <Title order={4} mb="xs">
            Group Types
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <Table striped highlightOnHover className={classes.statusTable}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Badge color="red" variant="light">
                      ADMIN
                    </Badge>
                  </td>
                  <td>
                    Full access to all organization resources and settings
                  </td>
                </tr>
                <tr>
                  <td>
                    <Badge color="blue" variant="light">
                      MEMBER
                    </Badge>
                  </td>
                  <td>
                    Authenticated users with custom permissions assigned via the
                    group
                  </td>
                </tr>
                <tr>
                  <td>
                    <Badge color="cyan" variant="light">
                      CONNECTED
                    </Badge>
                  </td>
                  <td>
                    Any authenticated user (logged in but not explicitly
                    assigned to a group)
                  </td>
                </tr>
                <tr>
                  <td>
                    <Badge color="gray" variant="light">
                      ANONYMOUS
                    </Badge>
                  </td>
                  <td>Unauthenticated users (public access level)</td>
                </tr>
              </tbody>
            </Table>
          </Card>

          <Title order={4} mb="xs">
            Permissions
          </Title>
          <Text mb="sm">
            Each group has a set of <Code>GroupPermission</Code> entries. A
            permission consists of an <Code>id</Code> (permission name) and a
            list of <Code>resources</Code> it applies to. This allows
            fine-grained control over which actions group members can perform on
            specific resource types.
          </Text>

          <Accordion variant="separated" mb="md">
            <Accordion.Item value="members">
              <Accordion.Control>
                <Group gap="xs">
                  <IconUsers size={16} />
                  <Text fw={500}>Managing Members</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Members can be added by email or user ID and assigned to a
                  specific group with optional custom permissions.
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>
                    <strong>Add Member</strong> - Invite by email or user ID,
                    assign to a group
                  </List.Item>
                  <List.Item>
                    <strong>Update Member</strong> - Change group assignment or
                    permissions (same mutation)
                  </List.Item>
                  <List.Item>
                    <strong>Remove Member</strong> - Delete membership by user
                    ID
                  </List.Item>
                </List>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="groups">
              <Accordion.Control>
                <Group gap="xs">
                  <IconUsersGroup size={16} />
                  <Text fw={500}>Managing Groups</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Custom groups can be created with specific permission sets.
                  The four default group types (ADMIN, MEMBER, CONNECTED,
                  ANONYMOUS) are always present.
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>
                    <strong>Create Group</strong> - Define name and permissions
                  </List.Item>
                  <List.Item>
                    <strong>Update Group</strong> - Modify name or permissions
                  </List.Item>
                  <List.Item>
                    <strong>Delete Group</strong> - Remove a custom group
                  </List.Item>
                </List>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== APPS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconApi size={18} />}
            title="Apps & OAuth"
            id="apps"
          />
          <Text mb="md">
            Organization apps are OAuth2/OIDC client applications that can
            authenticate users against the organization's identity system. Apps
            can be attached to groups to inherit specific permissions.
          </Text>

          <Title order={4} mb="xs">
            App Configuration
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <strong>Name</strong> - App display name
            </List.Item>
            <List.Item>
              <strong>Description</strong> - App description
            </List.Item>
            <List.Item>
              <strong>Redirect URLs</strong> - Allowed OAuth2 callback URLs
            </List.Item>
            <List.Item>
              <strong>Logout Redirect URLs</strong> - Post-logout redirect URLs
            </List.Item>
            <List.Item>
              <strong>Login URL</strong> - Custom login page URL
            </List.Item>
            <List.Item>
              <strong>Consent URL</strong> - Custom consent page URL
            </List.Item>
            <List.Item>
              <strong>Public</strong> - Whether the app is a public client (no
              secret required)
            </List.Item>
            <List.Item>
              <strong>Requires Consent</strong> - Whether users must explicitly
              approve access
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Attached Apps
          </Title>
          <Text mb="sm">
            Apps can be "attached" to organization groups. When attached, the
            app inherits the group's permissions, allowing API access scoped to
            those permissions.
          </Text>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mb="md"
          >
            App secrets can be regenerated at any time via{" "}
            <Code>resetOrganizationAppSecret</Code>. The old secret is
            immediately invalidated.
          </Alert>
        </div>

        {/* ===================== IDENTITY PROVIDERS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconKey size={18} />}
            title="Identity Providers"
            id="identity-providers"
          />
          <Text mb="md">
            Identity providers allow organizations to connect external
            authentication systems (e.g., Discord, Riot Games, Twitch) so that
            players can link their external accounts to their player profile.
          </Text>

          <Title order={4} mb="xs">
            Provider Configuration
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <strong>Name</strong> - Display name shown to users
            </List.Item>
            <List.Item>
              <strong>Description</strong> - Provider description
            </List.Item>
            <List.Item>
              <strong>Icon</strong> - Icon identifier for the provider
            </List.Item>
            <List.Item>
              <strong>Allow Login</strong> - Whether this provider can be used
              as a login method
            </List.Item>
            <List.Item>
              <strong>Enabled</strong> - Whether the provider is active
            </List.Item>
            <List.Item>
              <strong>Required for Player Validation</strong> - Whether linking
              this provider is mandatory for player profile validation
            </List.Item>
            <List.Item>
              <strong>OAuth2 / OpenID Configuration</strong> - External provider
              credentials and endpoints
            </List.Item>
          </List>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            Identity providers are used by tournament registration rules to
            verify player eligibility. For example, you can require that players
            have a linked League of Legends account with a specific rank.
          </Alert>
        </div>

        {/* ===================== LIMITS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconListDetails size={18} />}
            title="Limits & Subscriptions"
            id="limits"
          />
          <Text mb="md">
            Each organization has resource limits based on their subscription
            plan. Limits are enforced server-side and can be queried to display
            current usage.
          </Text>

          <Title order={4} mb="xs">
            Available Limit Types
          </Title>
          <SimpleGrid cols={{ base: 1, md: 3 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs" size="sm">
                Platform
              </Text>
              <List size="xs" spacing={2}>
                <List.Item>PLATFORMS</List.Item>
                <List.Item>PLATFORM_CUSTOM_DOMAINS</List.Item>
                <List.Item>PLATFORM_TEMPLATES</List.Item>
                <List.Item>ORGANIZATION_STORAGE_SIZE</List.Item>
                <List.Item>WHITE_LABEL</List.Item>
              </List>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs" size="sm">
                Users & Access
              </Text>
              <List size="xs" spacing={2}>
                <List.Item>REGISTERED_USERS</List.Item>
                <List.Item>ANONYMOUS_PLAYER_PROFILES</List.Item>
                <List.Item>ORGANIZATION_MEMBERS</List.Item>
                <List.Item>ORGANIZATION_GROUPS</List.Item>
                <List.Item>APPS</List.Item>
                <List.Item>ATTACHED_APPS</List.Item>
                <List.Item>CUSTOM_IDENTITY_PROVIDERS</List.Item>
              </List>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs" size="sm">
                Features
              </Text>
              <List size="xs" spacing={2}>
                <List.Item>CUSTOM_FIELDS</List.Item>
                <List.Item>TOURNAMENTS / STEPS / TEAMS</List.Item>
                <List.Item>EVENTS / VENUES / TICKETS</List.Item>
                <List.Item>LEADERBOARDS / SEASONS / BUCKETS</List.Item>
                <List.Item>CURRENCIES / ITEMS / SHOPS</List.Item>
                <List.Item>SKILL_RATINGS</List.Item>
                <List.Item>WEBHOOKS</List.Item>
              </List>
            </Card>
          </SimpleGrid>

          <GqlBlock>
            {`query ($limits: [OrganizationLimitsType!]) {
  organizationLimits(limits: $limits) {
    limits {
      id       # Limit type name
      current  # Current usage
      max      # Maximum allowed
    }
  }
}`}
          </GqlBlock>
        </div>

        {/* ===================== GRAPHQL API ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="GraphQL API Reference"
            id="graphql"
          />
          <Text mb="md">
            Complete list of all GraphQL operations related to organizations.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="org-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="green" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Text>Organization Queries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      userOrganizations (list)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Returns all organizations the authenticated user belongs
                      to. Used by the organization selector in the header.
                    </Text>
                    <GqlBlock>
                      {`query {
  userOrganizations {
    id
    name
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organization (detail)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Fetch a single organization by ID. Requires the{" "}
                      <Code>organization-id</Code> header.
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  organization(id: $id) {
    id
    name
    description
    ownerId
    stripeConfigured
    configuration {
      customFields {
        name, type, property, order, unique, required,
        public, editability, visibility
      }
    }
    groups { id, name, type, permissions { id, resources } }
    members { memberId, organizationId, group { id, name } }
    identityProviders(login: true) { id, name, icon }
    tournaments { id, title }
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="org-mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="red" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Text>Organization Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      createOrganization
                    </Text>
                    <GqlBlock>
                      {`mutation ($name: String!, $description: String!) {
  createOrganization(organization: {
    name: $name
    description: $description
    configuration: { customFields: [] }
  }) { id }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updateOrganization
                    </Text>
                    <GqlBlock>
                      {`mutation ($name: String!, $description: String!,
         $configuration: OrganizationConfigurationInput!) {
  updateOrganization(organization: {
    name: $name
    description: $description
    configuration: $configuration
  }) { id }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="member-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="cyan" className={classes.mutationBadge}>
                    MEMBERS
                  </Badge>
                  <Text>Member Queries & Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      organizationMembers (list)
                    </Text>
                    <GqlBlock>
                      {`query ($page: PageInfo!) {
  organizationMembers(page: $page) {
    nodes {
      memberId
      organizationId
      group { id, name, type }
      account { id, email }
      permissions { id, resources }
      createdAt
      updatedAt
    }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      setOrganizationMembership (upsert)
                    </Text>
                    <GqlBlock>
                      {`mutation ($email: EmailAddress, $userId: ID, $groupId: ID,
         $permissions: [GroupPermissionInput!]) {
  setOrganizationMembership(input: {
    email: $email
    userId: $userId
    groupId: $groupId
    permissions: $permissions
  }) {
    memberId
    organizationId
    group { id, name }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteOrganizationMembership
                    </Text>
                    <GqlBlock>
                      {`mutation ($userId: ID!) {
  deleteOrganizationMembership(input: { userId: $userId })
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="group-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="orange" className={classes.mutationBadge}>
                    GROUPS
                  </Badge>
                  <Text>Group Queries & Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      organizationGroups (list)
                    </Text>
                    <GqlBlock>
                      {`query ($page: PageInfo!) {
  organizationGroups(page: $page) {
    nodes {
      id
      name
      type          # ADMIN, MEMBER, CONNECTED, ANONYMOUS
      organizationId
      permissions { id, resources }
      createdAt
      updatedAt
    }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationGroup (detail)
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  organizationGroup(id: $id) {
    id
    name
    type
    organizationId
    permissions { id, resources }
    createdAt
    updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      createOrganizationGroup
                    </Text>
                    <GqlBlock>
                      {`mutation ($name: String!, $permissions: [GroupPermissionInput!]!) {
  createOrganizationGroup(input: {
    name: $name
    permissions: $permissions
  }) { id, name }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updateOrganizationGroup
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $name: String!, $permissions: [GroupPermissionInput!]!) {
  updateOrganizationGroup(id: $id, input: {
    name: $name
    permissions: $permissions
  }) { id, name }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteOrganizationGroup
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) {
  deleteOrganizationGroup(id: $id)
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="app-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="violet" className={classes.mutationBadge}>
                    APPS
                  </Badge>
                  <Text>App Queries & Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      organizationApps (list)
                    </Text>
                    <GqlBlock>
                      {`query ($page: PageInfo!) {
  organizationApps(page: $page) {
    nodes {
      id
      name
      description
      public
      secret
      organizationId
      configuration {
        redirectUrls
        logoutRedirectUrls
        scope
        metadata { loginUrl, consentUrl, public, requiresConsent, creatorId }
      }
      creator { id, email }
      createdAt
      updatedAt
    }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationApp (detail)
                    </Text>
                    <GqlBlock>
                      {`query ($id: String!) {
  organizationApp(id: $id) {
    id, name, description, public, secret,
    configuration {
      redirectUrls, logoutRedirectUrls, scope, name, organizationId,
      metadata { loginUrl, consentUrl, public, requiresConsent }
    }
    creator { id, email }
    createdAt, updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      createOrganizationApp
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: CreateOrganizationAppInput!) {
  createOrganizationApp(input: $input) { id, name, secret }
}

# CreateOrganizationAppInput:
# {
#   name: String!
#   description: String!
#   redirectUrls: [String!]!
#   logoutRedirectUrls: [String!]!
#   loginUrl: String!
#   consentUrl: String!
#   public: Boolean
#   requiresConsent: Boolean!
#   secret: String
# }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updateOrganizationApp
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: String!, $input: UpdateOrganizationAppInput!) {
  updateOrganizationApp(id: $id, input: $input) { id, name }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteOrganizationApp
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: String!) {
  deleteOrganizationApp(id: $id)
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      resetOrganizationAppSecret
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Regenerates the app secret. Returns the new secret string.
                      The old secret is immediately invalidated.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: String!) {
  resetOrganizationAppSecret(id: $id)
  # Returns: String! (new secret)
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="attached-app-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="pink" className={classes.mutationBadge}>
                    ATTACHED APPS
                  </Badge>
                  <Text>Attached App Queries & Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      organizationAttachedApps (list)
                    </Text>
                    <GqlBlock>
                      {`query ($page: PageInfo!) {
  organizationAttachedApps(page: $page) {
    nodes {
      appId
      organizationId
      app { id, name }
      group { id, name }
      groupId
      permissions { id, resources }
      createdAt
      updatedAt
    }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      setOrganizationAttachedApp (upsert)
                    </Text>
                    <GqlBlock>
                      {`mutation ($appId: String!, $groupId: ID,
         $permissions: [GroupPermissionInput!]) {
  setOrganizationAttachedApp(input: {
    appId: $appId
    groupId: $groupId
    permissions: $permissions
  }) {
    appId
    groupId
    permissions { id, resources }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteOrganizationAttachedApp
                    </Text>
                    <GqlBlock>
                      {`mutation ($appId: String!) {
  deleteOrganizationAttachedApp(input: { appId: $appId })
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="idp-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="teal" className={classes.mutationBadge}>
                    IDENTITY PROVIDERS
                  </Badge>
                  <Text>Identity Provider Queries & Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      identityProviders (list)
                    </Text>
                    <GqlBlock>
                      {`query {
  identityProviders {
    ... on OrganizationIdentityProvider {
      id
      name
      description
      icon
      enabled
      allowLogin
      requiredForPlayerValidation
      organizationId
      identityProviderId
      parentIdentityProvider { id, name, icon }
      configuration { ... }
      createdAt
      updatedAt
    }
    ... on PublicOrganizationIdentityProviderV2 {
      id
      name
      icon
      organizationId
    }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      identityProvider (detail)
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  identityProvider(id: $id) {
    id, name, description, icon, enabled,
    allowLogin, requiredForPlayerValidation,
    organizationId, identityProviderId,
    parentIdentityProvider { id, name, icon },
    configuration { ... }
    createdAt, updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      createIdentityProvider
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: CreateOrganizationIdentityProvider!) {
  createIdentityProvider(input: $input) { id, name }
}

# CreateOrganizationIdentityProvider:
# {
#   name: String!
#   description: String!
#   enabled: Boolean!
#   allowLogin: Boolean!
#   requiredForPlayerValidation: Boolean!
#   icon: String
#   identityProviderId: ID
#   oauth2Configuration: OAuthClientConfigurationInput
#   openidConfiguration: OpenidConfigurationInput
# }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updateIdentityProvider
                    </Text>
                    <GqlBlock>
                      {`mutation ($providerId: ID!, $input: UpdateOrganizationIdentityProvider!) {
  updateIdentityProvider(providerId: $providerId, input: $input) { id, name }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteIdentityProvider
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) {
  deleteIdentityProvider(id: $id)
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="subscription-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="yellow" className={classes.mutationBadge}>
                    SUBSCRIPTION
                  </Badge>
                  <Text>Subscription & Payment Operations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Alert
                  icon={<IconInfoCircle />}
                  color="yellow"
                  variant="light"
                  mb="md"
                >
                  These operations manage the organization's billing and
                  subscription plan via Stripe integration.
                </Alert>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      organizationCurrentSubscriptionItems
                    </Text>
                    <GqlBlock>
                      {`query {
  organizationCurrentSubscriptionItems {
    id
    name
    organizationId
    quantity
    createdAt
    updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationSubscriptionItemsUpdate
                    </Text>
                    <GqlBlock>
                      {`mutation ($items: [OrganizationSubscriptionItemUpdateInput!]!,
         $coupon: String, $referral: String) {
  organizationSubscriptionItemsUpdate(
    items: $items
    coupon: $coupon
    referral: $referral
  )
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationPaymentCollectionConfiguration
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Retrieve the Stripe payment collection configuration for
                      the organization (e.g., for event ticket payments).
                    </Text>
                    <GqlBlock>
                      {`query {
  organizationPaymentCollectionConfiguration {
    stripeAccountId
    stripeAccountStatus
  }
}

mutation {
  organizationPaymentCollectionConfigurationCreateOrUpdateAccount
  # Returns: String! (Stripe onboarding URL)
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      stripeCreatePortalSession
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Creates a Stripe billing portal session URL for the
                      organization to manage their subscription.
                    </Text>
                    <GqlBlock>
                      {`mutation {
  stripeCreatePortalSession
  # Returns: String! (Stripe portal URL)
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="skill-rating-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="grape" className={classes.mutationBadge}>
                    SKILL RATINGS
                  </Badge>
                  <Text>Skill Rating Operations</Text>
                  <Badge color="yellow" variant="light" size="xs">
                    Not used in console
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Alert
                  icon={<IconInfoCircle />}
                  color="yellow"
                  variant="light"
                  mb="md"
                >
                  Skill ratings provide an organization-level Elo/MMR system for
                  tracking player performance. These are API-only operations not
                  managed through the console UI.
                </Alert>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      organizationSkillRatings (list)
                    </Text>
                    <GqlBlock>
                      {`query {
  organizationSkillRatings {
    id
    name
    description
    organizationId
    startingRating
    kFactor
    createdAt
    updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationSkillRatingCreate
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: OrganizationSkillRatingCreateInput!) {
  organizationSkillRatingCreate(input: $input) { id, name }
}

# OrganizationSkillRatingCreateInput:
# {
#   name: String!
#   description: String!
#   startingRating: Float!
#   kFactor: Float!
# }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationSkillRatingsRegisterMatch
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Register a match result to update player skill ratings.
                    </Text>
                    <GqlBlock>
                      {`mutation ($organizationSkillRatingId: ID!,
         $input: OrganizationSkillRatingsRegisterMatchInput!) {
  organizationSkillRatingsRegisterMatch(
    organizationSkillRatingId: $organizationSkillRatingId
    input: $input
  ) {
    playerId
    newRating
    oldRating
    ratingChange
  }
}

# OrganizationSkillRatingsRegisterMatchInput:
# { teams: [{ players: [{ playerId: ID!, score: Float! }]! }]! }`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="input-types">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="gray" className={classes.mutationBadge}>
                    INPUTS
                  </Badge>
                  <Text>Input Types Reference</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      Organization Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreateOrganizationInput {
  name: String!
  description: String!
  configuration: OrganizationConfigurationInput!
}

input UpdateOrganizationInput {
  name: String!
  description: String!
  configuration: OrganizationConfigurationInput!
}

input OrganizationConfigurationInput {
  customFields: [PropertyInput!]!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      App Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreateOrganizationAppInput {
  name: String!
  description: String!
  redirectUrls: [String!]!
  logoutRedirectUrls: [String!]!
  loginUrl: String!
  consentUrl: String!
  public: Boolean
  requiresConsent: Boolean!
  secret: String
}

input UpdateOrganizationAppInput {
  name: String
  description: String
  redirectUrls: [String!]!
  logoutRedirectUrls: [String!]!
  loginUrl: String!
  consentUrl: String!
  requiresConsent: Boolean!
  secret: String
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Group & Membership Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreateOrganizationGroupInput {
  name: String!
  permissions: [GroupPermissionInput!]!
}

input SetOrganizationMembershipInput {
  email: EmailAddress
  userId: ID
  groupId: ID
  permissions: [GroupPermissionInput!]
}

input DeleteOrganizationMembershipInput {
  userId: ID!
}

input GroupPermissionInput {
  id: String!
  resources: [String!]!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Attached App Inputs
                    </Text>
                    <GqlBlock>
                      {`input SetOrganizationAttachedAppInput {
  appId: String!
  groupId: ID
  permissions: [GroupPermissionInput!]
}

input DeleteOrganizationAttachedAppInput {
  appId: String!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Identity Provider Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreateOrganizationIdentityProvider {
  name: String!
  description: String!
  enabled: Boolean!
  allowLogin: Boolean!
  requiredForPlayerValidation: Boolean!
  icon: String
  identityProviderId: ID
  oauth2Configuration: OAuthClientConfigurationInput
  openidConfiguration: OpenidConfigurationInput
}

input UpdateOrganizationIdentityProvider {
  name: String!
  description: String!
  enabled: Boolean!
  allowLogin: Boolean!
  requiredForPlayerValidation: Boolean!
  icon: String
  oauth2Configuration: OAuthClientConfigurationInput
  openidConfiguration: OpenidConfigurationInput
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Skill Rating Inputs
                    </Text>
                    <GqlBlock>
                      {`input OrganizationSkillRatingCreateInput {
  name: String!
  description: String!
  startingRating: Float!
  kFactor: Float!
}

input OrganizationSkillRatingUpdateInput {
  name: String!
  description: String!
  startingRating: Float!
  kFactor: Float!
}

input OrganizationSkillRatingsRegisterMatchInput {
  teams: [OrganizationSkillRatingsRegisterMatchTeamInput!]!
}

input OrganizationSkillRatingsRegisterMatchTeamInput {
  players: [OrganizationSkillRatingsRegisterMatchPlayerInput!]!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Subscription Inputs
                    </Text>
                    <GqlBlock>
                      {`input OrganizationSubscriptionItemUpdateInput {
  id: String!
  quantity: Float!
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== DATA MODEL ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Data Model"
            id="data-model"
          />
          <Text mb="md">
            Key types and enums used throughout the organization module. All
            types are generated from the GraphQL schema via codegen.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="enums">
              <Accordion.Control>Enums</Accordion.Control>
              <Accordion.Panel>
                <SimpleGrid cols={{ base: 1, md: 2 }}>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      OrganizationGroupType
                    </Text>
                    <GqlBlock>
                      {`ADMIN
ANONYMOUS
CONNECTED
MEMBER`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      OrganizationLimitsType
                    </Text>
                    <GqlBlock>
                      {`ANONYMOUS_PLAYER_PROFILES
APPS
ATTACHED_APPS
CURRENCIES
CUSTOM_FIELDS
CUSTOM_IDENTITY_PROVIDERS
EVENTS_MAX_GROUP_SIZE
EVENTS_MAX_REGISTRATION_RULES
EVENTS_MAX_TICKETS
EVENTS_MAX_TICKETS_TYPES
EVENTS_MAX_TICKET_CONFIGURATION_CUSTOM_FIELDS
EVENT_VENUES
EVENT_VENUE_MAX_SEATS
EVENT_VENUE_MAX_SEAT_TYPES
ITEMS
ITEM_CATEGORIES
LEADERBOARDS
LEADERBOARD_BUCKETS
LEADERBOARD_ENTRIES
LEADERBOARD_SEASONS
ORGANIZATION_GROUPS
ORGANIZATION_MEMBERS
ORGANIZATION_STORAGE_SIZE
PLATFORMS
PLATFORM_CUSTOM_DOMAINS
PLATFORM_TEMPLATES
REGISTERED_USERS
SHOPS
SHOP_CATEGORIES
SHOP_PRODUCTS
SKILL_RATINGS
TOURNAMENT_REGISTRATION_RULES
TOURNAMENT_STEPS
TOURNAMENT_STEP_GROUPS
TOURNAMENT_TEAMS_REGISTERED
TOURNAMENT_TEAM_SIZE
WEBHOOKS
WHITE_LABEL`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      OrganizationLimitType
                    </Text>
                    <GqlBlock>
                      {`BOOLEAN
BYTES
DAYS
NUMBER`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      PropertyType (custom fields)
                    </Text>
                    <GqlBlock>
                      {`STRING
NUMBER
BOOLEAN
DATE
COUNTRY
EMAIL
PHONE
URL`}
                    </GqlBlock>
                  </div>
                </SimpleGrid>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="entities">
              <Accordion.Control>Entity Hierarchy</Accordion.Control>
              <Accordion.Panel>
                <GqlBlock>
                  {`Organization
├── id: ID!
├── name: String!
├── description: String!
├── ownerId: ID
├── stripeConfigured: Boolean
├── configuration: OrganizationConfiguration
│   └── customFields: [Property!]!
│       ├── name: String!
│       ├── property: String!
│       ├── type: PropertyType!
│       ├── order: Float!
│       ├── visibility: PropertyVisibility!
│       ├── editability: PropertyEditability!
│       ├── required: Boolean!
│       ├── unique: Boolean!
│       └── public: Boolean!
├── groups: [OrganizationGroup!]!
│   ├── id: ID!
│   ├── name: String!
│   ├── type: OrganizationGroupType!
│   ├── permissions: [GroupPermission!]!
│   │   ├── id: String!
│   │   └── resources: [String!]!
│   └── organizationId: ID!
├── members: [OrganizationMember!]!
│   ├── memberId: ID!
│   ├── organizationId: ID!
│   ├── group: OrganizationGroup!
│   ├── account: Account!
│   └── permissions: [GroupPermission!]
├── identityProviders: [OrganizationIdentityProvider!]!
│   ├── id: ID!
│   ├── name: String!
│   ├── description: String!
│   ├── icon: String
│   ├── enabled: Boolean!
│   ├── allowLogin: Boolean!
│   ├── requiredForPlayerValidation: Boolean!
│   ├── identityProviderId: ID
│   ├── parentIdentityProvider: PublicIdentityProvider
│   └── configuration: IdentityProviderConfigurations
├── apps: [OrganizationApp!]!  (via organizationApps query)
│   ├── id: String!
│   ├── name: String!
│   ├── description: String!
│   ├── public: Boolean
│   ├── secret: String
│   ├── organizationId: ID!
│   ├── creator: Account!
│   └── configuration: OrganizationAppConfig
│       ├── redirectUrls: [String!]!
│       ├── logoutRedirectUrls: [String!]!
│       ├── scope: String!
│       └── metadata: OrganizationAppConfigMetadata
│           ├── loginUrl: String!
│           ├── consentUrl: String!
│           ├── public: Boolean!
│           └── requiresConsent: Boolean!
├── attachedApps: [OrganizationAttachedApp!]!  (via organizationAttachedApps)
│   ├── appId: String!
│   ├── app: OrganizationApp!
│   ├── groupId: ID!
│   ├── group: OrganizationGroup!
│   ├── permissions: [GroupPermission!]
│   └── organizationId: ID!
├── subscription: [OrganizationSubscriptionItem!]!
│   ├── id: String!
│   ├── name: String!
│   ├── organizationId: ID!
│   └── quantity: Float!
├── limits: OrganizationLimits
│   └── limits: [OrganizationLimit!]!
│       ├── id: String!
│       ├── current: Float!
│       └── max: Float!
├── skillRatings: [OrganizationSkillRating!]!
│   ├── id: ID!
│   ├── name: String!
│   ├── description: String!
│   ├── organizationId: ID!
│   ├── startingRating: Float!
│   └── kFactor: Float!
└── paymentCollectionConfiguration: OrganizationPaymentCollectionConfiguration
    ├── stripeAccountId: String
    └── stripeAccountStatus: String`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== CUSTOM FIELDS & METADATA ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Custom Fields & Metadata (Data Service)"
            id="custom-fields-metadata"
          />
          <Text mb="md">
            In addition to the organization-level player profile custom fields
            configured in Settings, the platform provides a separate{" "}
            <Text fw={700} span>
              Custom Fields
            </Text>{" "}
            and{" "}
            <Text fw={700} span>
              Object Metadata
            </Text>{" "}
            system from the Data service that applies to all entity types across
            your organization.
          </Text>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>
              <Text fw={600} span>
                Custom Fields (Data service)
              </Text>{" "}
              — Structured, typed fields with validation, visibility, and
              editability controls. Define schemas in Data &gt; Custom Fields
              for any supported object type (Tournaments, Events, Players,
              Leaderboards, and more).
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Object Metadata
              </Text>{" "}
              — Flexible key-value store for attaching arbitrary data to any
              supported entity. Each entry has a type (String, Boolean, URL,
              Image URL) and visibility (Public or Private).
            </List.Item>
          </List>
          <Alert icon={<IconInfoCircle />} color="cyan" variant="light">
            See the{" "}
            <Anchor href="/data/documentations">Data Documentation</Anchor> for
            the full Custom Fields & Metadata API reference, supported object
            types, and GraphQL queries/mutations.
          </Alert>
        </div>
      </Stack>
    </div>
  );
};
